import { useEffect, useState } from "react";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useGame } from "../services/gameContext";
import GamePicker from "../components/GamePicker";
import PopBattleSeason from "../components/PopBattleSeason";
import PopBattlePointChart from "../components/PopBattlePointChart";
import PopBattleLevelChart from "../components/PopBattleLevelChart";

// Battle calendar — read-only public page. Cloud Functions admin tooling
// writes events; we just list them grouped by upcoming vs running vs past.
export default function Calendar() {
  const { game, popBattleDocs } = useGame();
  const popBattleDoc = popBattleDocs[game] || null;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const now = new Date();
      const q = query(
          collection(db, "pop_market_calendar"),
          where("game", "in", [game, "BOTH"]),
          orderBy("startsAt", "asc"),
      );
      const snap = await getDocs(q);
      if (cancelled) return;
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEvents(all.filter((e) =>
          (e.endsAt?.toDate?.() || new Date(e.endsAt) || now) > now,
      ));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [game]);

  return (
    <div className="container" style={{ paddingBottom: 64 }}>
      <header style={{ padding: "8px 0 24px", borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
        <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 800 }}>
          Battle Calendar
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          Annual Galas, 1v1 {game === "PUBG" ? "PK" : "battle"} windows, free-pop events. Plan stock; plan spend.
        </p>
      </header>

      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 20 }}>
        <GamePicker />
      </div>

      {popBattleDoc ? (
        <PopBattleSeason game={game} doc={popBattleDoc} />
      ) : (
        <>
          <PopBattlePointChart game={game} />
          <PopBattleLevelChart game={game} />
        </>
      )}

      {loading && <div className="muted">Loading…</div>}
      {!loading && events.length === 0 && !popBattleDoc && (
        <div className="card dim" style={{ textAlign: "center", padding: "48px 16px" }}>
          No upcoming events on file for {game}. Check back soon, or follow the
          PlayAware app for in-game patch alerts.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {events.map((e) => <CalendarRow key={e.id} event={e} />)}
      </div>
    </div>
  );
}

function CalendarRow({ event }) {
  const start = event.startsAt?.toDate?.() || new Date(event.startsAt);
  const end   = event.endsAt?.toDate?.()   || new Date(event.endsAt);
  const now   = new Date();
  const live  = now >= start && now < end;
  return (
    <div className="card" style={{ display: "flex", gap: 14, alignItems: "center" }}>
      <div style={{ width: 56, textAlign: "center", flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
          {start.toLocaleDateString(undefined, { month: "short" })}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{start.getDate()}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
          <span style={{ fontWeight: 700 }}>{event.name}</span>
          {live && <span className="tag" style={{ background: "rgba(43,181,98,0.2)", color: "var(--ok)" }}>LIVE</span>}
          <span className={`tag ${event.game === "BGMI" ? "bgmi" : event.game === "PUBGM" ? "pubgm" : ""}`}>
            {event.game}
          </span>
        </div>
        <div className="dim" style={{ fontSize: 12 }}>
          {start.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
          {" → "}
          {end.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
        </div>
        {event.description && (
          <p style={{ margin: "6px 0 0", fontSize: 13 }}>{event.description}</p>
        )}
      </div>
    </div>
  );
}
